import { Box, Tooltip } from "@mui/material";
import { addDays, differenceInDays, format, getDay, startOfWeek } from "date-fns";
import { memo, useMemo } from "react";
import { DailyMatchesEntry } from "../../api/ActivityService";

interface Props {
  dailyMatches: DailyMatchesEntry[];
  start: Date;
  end: Date;
}

// Ranges wider than this switch from per-day cells to per-week buckets.
const WEEKLY_THRESHOLD_DAYS = 90;
const DAY_CELL_WIDTH = 6;
const WEEK_CELL_WIDTH = 14;

// Fixed 0-100% response-rate ramp: a cell falls into the first band whose max
// covers its rate. Single source for the cell colors and the legend.
const HEAT_BANDS = [
  { max: 0, color: "#f5f5f5", label: "0%" },
  { max: 0.25, color: "#cce5cc", label: "1-25%" },
  { max: 0.5, color: "#66b266", label: "26-50%" },
  { max: 0.75, color: "#2e7d32", label: "51-75%" },
  { max: 1, color: "#1b5e20", label: "76-100%" },
];
// Cells with no rate: no snapshot for the day, or a snapshot with zero offers
// (no denominator). The tooltip tells the two apart.
const NEUTRAL_COLOR = "#eaeaea";

export interface HeatCell {
  key: string;
  label: string; // "dd.MM" (day) or "Week of dd.MM" (week)
  hasData: boolean; // a snapshot exists for this cell — distinct from zero responses
  responses: number; // confirmed + rejected
  rate: number | null; // responses / matches; null when matches is 0 (no denominator)
  confirmed: number;
  rejected: number;
  matches: number;
  filtered: number | undefined; // undefined when no snapshot in the cell recorded it
  weekly: boolean;
}

const dayOfWeekSuffix = (d: Date): string => {
  const day = getDay(d);
  if (day === 0) return " (Sun)";
  if (day === 6) return " (Sat)";
  return "";
};

const responseRate = (responses: number, matches: number): number | null =>
  matches > 0 ? responses / matches : null;

/**
 * Turn a consultant's daily snapshots into the cells actually rendered for the
 * given range (per-day, or per-week when the range is wide). Each cell carries
 * its own response rate, so cells are independent of each other.
 */
export const buildCells = (
  dailyMatches: DailyMatchesEntry[],
  start: Date,
  end: Date,
): HeatCell[] => {
  const byDate = new Map<string, DailyMatchesEntry>();
  for (const entry of dailyMatches) byDate.set(entry.date, entry);

  const rangeDays = differenceInDays(end, start) + 1;

  if (rangeDays > WEEKLY_THRESHOLD_DAYS) {
    const cells: HeatCell[] = [];
    let cursor = startOfWeek(start, { weekStartsOn: 1 });
    while (cursor <= end) {
      let confirmed = 0;
      let rejected = 0;
      let matches = 0;
      let filtered: number | undefined = undefined;
      let hasData = false;
      for (let i = 0; i < 7; i++) {
        const day = addDays(cursor, i);
        if (day < start || day > end) continue;
        const entry = byDate.get(format(day, "yyyy-MM-dd"));
        if (!entry) continue;
        hasData = true;
        confirmed += entry.confirmed;
        rejected += entry.rejected;
        matches += entry.matches;
        if (entry.filtered !== undefined) filtered = (filtered ?? 0) + entry.filtered;
      }
      const responses = confirmed + rejected;
      cells.push({
        key: cursor.toISOString(),
        label: `Week of ${format(cursor, "dd.MM")}`,
        hasData,
        responses,
        rate: responseRate(responses, matches),
        confirmed,
        rejected,
        matches,
        filtered,
        weekly: true,
      });
      cursor = addDays(cursor, 7);
    }
    return cells;
  }

  const cells: HeatCell[] = [];
  for (let i = 0; i < rangeDays; i++) {
    const day = addDays(start, i);
    const key = format(day, "yyyy-MM-dd");
    const entry = byDate.get(key);
    const hasData = entry !== undefined;
    const responses = hasData ? entry!.confirmed + entry!.rejected : 0;
    const matches = entry?.matches ?? 0;
    cells.push({
      key,
      label: `${format(day, "dd.MM")}${hasData ? "" : dayOfWeekSuffix(day)}`,
      hasData,
      responses,
      rate: responseRate(responses, matches),
      confirmed: entry?.confirmed ?? 0,
      rejected: entry?.rejected ?? 0,
      matches,
      filtered: entry?.filtered,
      weekly: false,
    });
  }
  return cells;
};

const cellTooltip = (cell: HeatCell): string => {
  if (!cell.hasData) return `${cell.label} (no snapshot)`;
  if (cell.rate === null) return `${cell.label} — no offers received`;
  let text =
    `${cell.label} — ${cell.responses}/${cell.matches} responded ` +
    `(${Math.round(cell.rate * 100)}%): ${cell.confirmed} accepted, ${cell.rejected} declined`;
  if (cell.filtered !== undefined) text += ` · ${cell.filtered} filtered out`;
  return text;
};

// A rate above 1 cannot come from well-formed data; cap it at the top band.
const cellColor = (cell: HeatCell): string =>
  cell.rate === null
    ? NEUTRAL_COLOR
    : (HEAT_BANDS.find((band) => cell.rate! <= band.max) ?? HEAT_BANDS[HEAT_BANDS.length - 1])
        .color;

const SWATCH_SX = { display: "inline-block", width: 10, height: 10, verticalAlign: "middle" };

/** The fixed legend for the heat strip, drawn from the same bands as the cells. */
export function HeatLegend() {
  return (
    <>
      <strong>Response rate (accepted + declined / offers received):</strong>
      {HEAT_BANDS.map((band) => (
        <span key={band.label}>
          <Box component="span" sx={{ ...SWATCH_SX, bgcolor: band.color, ml: 1, mr: 0.5 }} />
          {band.label}
        </span>
      ))}
      <Box component="span" sx={{ ...SWATCH_SX, bgcolor: NEUTRAL_COLOR, ml: 1.5, mr: 0.5 }} /> no
      offers / no snapshot
    </>
  );
}

function ActivityHeatStrip({ dailyMatches, start, end }: Props) {
  const cells = useMemo(() => buildCells(dailyMatches, start, end), [dailyMatches, start, end]);
  const cellWidth = cells[0]?.weekly ? WEEK_CELL_WIDTH : DAY_CELL_WIDTH;

  return (
    <Box sx={{ display: "flex", gap: "1px" }}>
      {cells.map((cell) => (
        <Tooltip key={cell.key} title={cellTooltip(cell)}>
          <Box
            sx={{
              width: cellWidth,
              height: 14,
              backgroundColor: cellColor(cell),
            }}
          />
        </Tooltip>
      ))}
    </Box>
  );
}

export default memo(ActivityHeatStrip);
